import { DIMENSION_META } from './feederScoreShared';

export const SUPPORTED_RW_CONTRACT_VERSIONS = new Set([1]);
const LEGACY_RW_CONTRACT_VERSION = 1;
const LEGACY_RW_CONTRACT_V1_ALGO_VERSIONS = new Set([
  'feeder_score_reason_audit_v1',
  'feeder_score_reason_audit_v2',
]);
export const RECALC_STORAGE_KEY = 'cloudFeederScoreRecalcParams';

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const rwContractVersion = (kernel) => {
  if (!kernel) return null;
  if (kernel.contract_version == null && LEGACY_RW_CONTRACT_V1_ALGO_VERSIONS.has(kernel.algo_version)) {
    return LEGACY_RW_CONTRACT_VERSION;
  }
  const version = Number(kernel.contract_version);
  return Number.isInteger(version) ? version : null;
};

const kernelAlgoVersions = (kernel) => {
  const versions = Array.isArray(kernel?.algo_versions)
    ? kernel.algo_versions.filter(Boolean).map(String)
    : [];
  if (versions.length) return versions;
  return kernel?.algo_version ? [String(kernel.algo_version)] : ['__unknown__'];
};

export const hasMixedRules = (kernel) => (
  kernel?.config_version === '__mixed__'
  || kernel?.algo_version === '__mixed__'
  || kernel?.algo_version === '__unknown__'
  || (kernel != null && !kernel.algo_version)
);

export const isRecomputable = (kernel) => (
  kernel
  && SUPPORTED_RW_CONTRACT_VERSIONS.has(rwContractVersion(kernel))
  && kernel.dims
  && Object.keys(kernel.dims).length > 0
);

export const reweight = (kernel, params) => {
  if (!isRecomputable(kernel)) return null;
  const weights = params?.weights || {};
  const base = number(params?.base, number(kernel.base, 0));
  const sfrCap = number(params?.sfrCap, 15);
  const sfrPerPct = number(params?.sfrPerPct, 0.2);
  const allowOverscore = params?.allowOverscore !== false;
  let num = 0;
  let den = 0;

  Object.entries(kernel.dims || {}).forEach(([dim, item]) => {
    const w = Math.max(0, number(weights[dim], 0));
    const cov = clamp(number(item?.cov, 0), 0, 1);
    const q = clamp(number(item?.q, 0), 0, 1);
    num += w * cov * q;
    den += w * cov;
  });

  const qValue = den > 0 ? num / den : 1;
  const bonus = Math.min(Math.max(0, number(kernel.deliv_pct, 0) - 100) * sfrPerPct, sfrCap) * qValue;
  const ceiling = allowOverscore ? 100 + sfrCap : 100;
  return clamp(base + (100 - base) * qValue + bonus, base, ceiling);
};

export const aggregateRw = (kernels, aggregation = 'food_weighted') => {
  const children = (kernels || []).filter((kernel) => kernel?.dims && Object.keys(kernel.dims).length);
  if (!children.length) return null;
  const weighted = aggregation === 'food_weighted';
  let totalWeight = 0;
  let foodTotal = 0;
  let delivNum = 0;
  const algoVersions = new Set();
  const contractVersions = new Set();
  const configVersions = new Set();
  const bases = new Set();
  const dimKeys = new Set();

  children.forEach((kernel) => {
    const food = Math.max(0, number(kernel.food_kg, 0));
    const weight = (weighted ? food : 1) || 1;
    totalWeight += weight;
    foodTotal += food;
    delivNum += weight * number(kernel.deliv_pct, 0);
    kernelAlgoVersions(kernel).forEach((version) => algoVersions.add(version));
    contractVersions.add(rwContractVersion(kernel));
    if (kernel.config_version) configVersions.add(kernel.config_version);
    bases.add(number(kernel.base, 0));
    Object.keys(kernel.dims || {}).forEach((key) => dimKeys.add(key));
  });
  if (totalWeight <= 0 || contractVersions.size !== 1) return null;
  const [contractVersion] = contractVersions;
  if (!SUPPORTED_RW_CONTRACT_VERSIONS.has(contractVersion)) return null;

  const dims = {};
  [...dimKeys].sort().forEach((dim) => {
    let covNum = 0;
    let qNum = 0;
    let qDen = 0;
    children.forEach((kernel) => {
      const item = kernel.dims?.[dim] || {};
      const food = Math.max(0, number(kernel.food_kg, 0));
      const weight = (weighted ? food : 1) || 1;
      const cov = clamp(number(item.cov, 0), 0, 1);
      const q = clamp(number(item.q, 0), 0, 1);
      covNum += weight * cov;
      qNum += weight * cov * q;
      qDen += weight * cov;
    });
    dims[dim] = {
      q: qDen > 0 ? Number((qNum / qDen).toFixed(6)) : 0,
      cov: Number((covNum / totalWeight).toFixed(6)),
    };
  });

  const sortedAlgoVersions = [...algoVersions].sort();
  return {
    algo_version: sortedAlgoVersions.length === 1 ? sortedAlgoVersions[0] : '__mixed__',
    algo_versions: sortedAlgoVersions,
    contract_version: contractVersion,
    config_version: configVersions.size === 1 ? [...configVersions][0] : '__mixed__',
    base: bases.size === 1 ? [...bases][0] : null,
    food_kg: Number((weighted ? foodTotal : totalWeight).toFixed(6)),
    deliv_pct: Number((delivNum / totalWeight).toFixed(6)),
    dims,
  };
};

const reactionSamples = (reactionTime) => {
  if (!reactionTime || typeof reactionTime !== 'object') return [];
  if (Array.isArray(reactionTime.samples_sec)) return reactionTime.samples_sec;
  const avg = reactionTime.mean_sec ?? reactionTime.avg;
  const n = Number(reactionTime.n || 0);
  if (avg == null || avg === '' || !Number.isFinite(Number(avg)) || n <= 0) return [];
  return Array.from({ length: n }, () => Number(avg));
};

const summarizeReactionSamples = (samples) => {
  const clean = (samples || []).filter((value) => value != null && value !== '').map(Number).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!clean.length) return { n: 0, samples_sec: [] };
  return {
    n: clean.length,
    mean_sec: clean.reduce((sum, value) => sum + value, 0) / clean.length,
    p95_sec: clean[Math.min(clean.length - 1, Math.floor((clean.length - 1) * 0.95))],
    max_sec: clean[clean.length - 1],
    samples_sec: clean,
  };
};

const areaFromCenterId = (centerId) => {
  const parts = String(centerId || '').split('-');
  return parts.length >= 2 ? parts.slice(0, 2).join('-') : centerId;
};

const regroupRegionCentersToAreas = (scoreData, params) => {
  const next = {};
  Object.entries(scoreData || {}).forEach(([day, rows]) => {
    const groups = {};
    Object.entries(rows || {}).forEach(([rowId, doc]) => {
      const areaId = areaFromCenterId(rowId);
      const group = groups[areaId] || (groups[areaId] = {
        scoreNum: 0,
        scoreDen: 0,
        food: 0,
        alarmTotal: 0,
        alarmUnattended: 0,
        reaction: [],
        n: 0,
        rws: [],
      });
      const score = doc?.status === 'provisional' ? null : reweight(doc?.rw, params);
      const food = Math.max(0, number(doc?.delivered_food_kg, number(doc?.rw?.food_kg, 0)));
      const weight = params?.aggregation === 'mean' ? 1 : (food || 1);
      group.food += food;
      group.alarmTotal += number(doc?.alarmas_total ?? doc?.reasons_summary?.alarmas_total, 0);
      group.alarmUnattended += number(doc?.alarmas_no_atendidas ?? doc?.reasons_summary?.alarmas_no_atendidas, 0);
      group.reaction.push(...reactionSamples(doc?.reaction_time));
      if (doc?.rw) group.rws.push(doc.rw);
      if (score != null) {
        group.scoreNum += score * weight;
        group.scoreDen += weight;
        group.n += 1;
      }
    });

    next[day] = {};
    Object.entries(groups).forEach(([areaId, group]) => {
      const rw = aggregateRw(group.rws, params?.aggregation);
      const score = group.scoreDen > 0 ? group.scoreNum / group.scoreDen : null;
      next[day][areaId] = {
        scope: 'area',
        name: areaId,
        status: score == null ? 'fasted' : 'final',
        score: score == null ? null : Number(score.toFixed(2)),
        aggregation: params?.aggregation || 'food_weighted',
        delivered_food_kg: Number(group.food.toFixed(6)),
        alarmas_total: group.alarmTotal,
        alarmas_no_atendidas: group.alarmUnattended,
        reaction_time: summarizeReactionSamples(group.reaction),
        n_centros_scored: group.n,
        rw,
        recalc_exploratory: score != null,
        recalc_warning: hasMixedRules(rw) ? 'mixed_rules' : (score == null ? 'not_recomputable' : null),
      };
    });
  });
  return next;
};

export const applyRecalcToScoreData = (scoreData, params, options = {}) => {
  if (!params || params.mode === 'center') return scoreData || {};
  if (options.groupRegionCenters) return regroupRegionCentersToAreas(scoreData, params);
  const next = {};
  Object.entries(scoreData || {}).forEach(([day, rows]) => {
    next[day] = {};
    Object.entries(rows || {}).forEach(([rowId, doc]) => {
      const hasCycleDelivery = doc?.scope !== 'jaula'
        || (Array.isArray(doc?.cycles) && doc.cycles.every((cycle) => cycle?.porc_entregado != null));
      const score = doc?.status === 'provisional' || !hasCycleDelivery ? null : reweight(doc?.rw, params);
      next[day][rowId] = score == null
        ? { ...doc, recalc_warning: 'not_recomputable' }
        : {
            ...doc,
            score: Number(score.toFixed(2)),
            recalc_exploratory: true,
            recalc_warning: hasMixedRules(doc?.rw) ? 'mixed_rules' : null,
          };
    });
  });
  return next;
};

export const defaultRecalcParams = () => ({
  base: 20,
  weights: Object.fromEntries(Object.entries(DIMENSION_META).map(([key, meta]) => [key, meta.defaultWeight || 0])),
  allowOverscore: true,
  sfrCap: 15,
  sfrPerPct: 0.2,
  aggregation: 'food_weighted',
  mode: 'center',
  exact: false,
});
